"use client";

import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { useProviders } from "@/hooks/use-providers";
import { formatStaffName } from "@/lib/format-data";
import { parseNotificationContent } from "@/lib/notification-reference";
import { cx } from "@/lib/tailwind-utils";
import { SuggestionItem } from "@/types/suggestions";
import { CustomMention } from "./mention/mention";
import { ReferenceMention } from "./mention/reference-mention";
import { suggestion } from "./suggestion";
interface TaskMentionEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onEnter?: () => void;
  /** Renders the content without letting anyone type into it. */
  editable?: boolean;
}

/** Escape a string for safe interpolation into an HTML attribute value. */
function escapeHtmlAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Convert single-brace provider mentions (`{provider_id}`) into chip markup. */
function transformProviderMentions(text: string, suggestions: SuggestionItem[]): string {
  const mentionRegex = /\{([^}]+)\}/g;
  return text.replace(mentionRegex, (match, variableId: string) => {
    const suggestion = suggestions.find(s => s.id === variableId);
    const label = suggestion?.label;
    if (!label) {
      return `{${variableId}}`;
    }
    return `<mention-component data-id="${variableId}" data-label="${suggestion.label}" data-content="${suggestion.content}" data-type="${suggestion.type}">@${suggestion.label}</mention-component>`;
  });
}
export function transformContentToMentions(content: string, suggestions: SuggestionItem[]) {
  // Split on `{{type:display:id}}` reference tokens first so the single-brace
  // provider-mention regex below never sees (and corrupts) them. Reference
  // segments become atomic chips; plain-text segments keep their `{id}` mentions.
  //
  // Text segments keep their delimiter escapes: this editor persists
  // `editor.getText()` on every change, so unescaping here would write raw
  // `{{…}}` back to storage and reopening the task would render text the patient
  // typed as a real reference chip. Display-only renderers unescape; this one
  // round-trips, so it must not.
  const contentWithMentions = parseNotificationContent(content, {
    unescapeText: false
  }).map(segment => {
    if (segment.type === "reference") {
      const {
        type,
        display,
        id
      } = segment.reference;
      return `<reference-mention data-type="${escapeHtmlAttr(type)}" data-display="${escapeHtmlAttr(display)}" data-id="${escapeHtmlAttr(id)}"></reference-mention>`;
    }
    return transformProviderMentions(segment.value, suggestions);
  }).join("");

  // Convert newlines to <br />
  return contentWithMentions.replace(/\n/g, "<br />");
}
export function TaskMentionEditor({
  value,
  onChange,
  placeholder,
  className,
  onEnter,
  editable = true
}: TaskMentionEditorProps) {
  const {
    providers
  } = useProviders();
  const suggestionRef = useRef<SuggestionItem[]>([]);

  // Transform providers to suggestion items
  useEffect(() => {
    suggestionRef.current = providers.map(provider => ({
      id: provider.id,
      label: formatStaffName(provider),
      content: provider.roles.map(role => role.name).join(", ") || "",
      type: "TEAM" as const
    }));
  }, [providers]);
  const getSuggestionItems = ({
    query
  }: {
    query: string;
  }): SuggestionItem[] => {
    const lowercaseQuery = query.toLowerCase();
    return suggestionRef.current.filter(item => item.label.toLowerCase().includes(lowercaseQuery) || item.content.toLowerCase().includes(lowercaseQuery));
  };
  const editor_0 = useEditor({
    immediatelyRender: false,
    editable,
    extensions: [Document, Paragraph, Text, HardBreak, History, Placeholder.configure({
      placeholder
    }), ReferenceMention, CustomMention.configure({
      renderText: ({
        node
      }) => {
        return `{${node.attrs.id}}`;
      },
      suggestion: {
        items: getSuggestionItems,
        ...suggestion
      }
    })],
    content: transformContentToMentions(value, suggestionRef.current),
    editorProps: {
      attributes: {
        class: cx(
        // Base styles
        "prose prose-sm", "max-w-none w-full overflow-hidden", "rounded-md", "border border-border hover:border-border", "px-2 py-1.5 m-0", "min-h-[2rem] overflow-y-auto resize-none break-words",
        // Remove focus ring styles
        "focus:ring-0 focus:outline-none focus:border-border",
        // Custom classes
        className)
      },
      handleKeyDown: (view, event) => {
        // Handle Enter key
        if (event.key === "Enter" && !event.shiftKey) {
          // Check if suggestion menu is active
          const suggestionMenu = document.querySelector(".tippy-box");
          if (suggestionMenu) {
            return false; // Let the suggestion menu handle the enter key
          }

          // If no suggestion menu is active and we have an onEnter handler, use it
          if (onEnter) {
            event.preventDefault();
            event.stopPropagation();
            onEnter();
            return true;
          }
        }
        return false;
      }
    },
    onUpdate: ({
      editor
    }) => {
      const text = editor.getText();
      onChange(text);
    }
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (!editor_0) return;
    const currentText = editor_0.getText();
    // If value is empty and editor is not, or if they differ significantly
    if (value === "" && currentText !== "" || value !== "" && value !== currentText) {
      const transformed = transformContentToMentions(value, suggestionRef.current);
      editor_0.commands.setContent(transformed);
    }
  }, [value, editor_0]);

  // `useEditor` reads `editable` once. Permissions resolve after the first
  // render, so without this the editor stays as it was created.
  useEffect(() => {
    editor_0?.setEditable(editable);
  }, [editor_0, editable]);
  return <div className="relative">
      <EditorContent editor={editor_0} />
    </div>;
}